import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BellIcon as Bell, CheckCircleIcon as CheckCircle, CheckIcon as Check } from "@phosphor-icons/react";

const SEV_DOT = { critical: "bg-red-500", warning: "bg-amber-500", info: "bg-blue-500" };

export function AlertsBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const loadCount = useCallback(() => api.get("/notifications/unread-count").then(({ data }) => setCount(data.count)).catch(() => {}), []);
  const loadItems = useCallback(() => api.get("/notifications?limit=30").then(({ data }) => setItems(data)).catch(() => {}), []);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 60000); return () => clearInterval(t); }, [loadCount]);
  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); loadItems(); loadCount(); };
  const markAll = async () => { await api.post("/notifications/read-all"); loadItems(); loadCount(); };
  const openNotif = async (n) => {
    if (!n.link) return;
    if (!n.letto) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ } loadCount(); }
    setOpen(false);
    nav(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="alerts-bell" className="relative h-9 w-9 flex items-center justify-center rounded-sm hover:bg-secondary transition-colors">
          <Bell size={20} weight="bold" className="text-muted-foreground" />
          {count > 0 && (
            <span data-testid="alerts-badge" className="absolute top-0 right-0 min-w-[17px] h-[17px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-sm shadow-lg" data-testid="alerts-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h4 className="font-heading font-semibold text-sm">Avvisi{count > 0 ? ` · ${count} non letti` : ""}</h4>
          {items.some((i) => !i.letto) && (
            <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-primary hover:underline flex items-center gap-1">
              <Check size={13} /> Segna tutti letti
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">Nessun avviso.</p>}
          {items.map((n) => (
            <div key={n.id} data-testid="alert-item" className={`px-4 py-3 border-b border-border flex gap-3 ${n.letto ? "opacity-55" : "bg-secondary/40"}`}>
              <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${SEV_DOT[n.severita] || "bg-blue-500"}`} />
              <div className={`flex-1 min-w-0 ${n.link ? "cursor-pointer" : ""}`} onClick={() => openNotif(n)} data-testid={n.link ? "alert-item-link" : undefined}>
                <div className="text-sm font-medium leading-tight">{n.titolo}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{n.messaggio}{n.cliente_nome ? ` · ${n.cliente_nome}` : ""}</div>
                {n.link && <div className="text-[11px] text-primary mt-0.5 hover:underline">Apri →</div>}
              </div>
              {!n.letto && (
                <button title="Segna come letto" data-testid="mark-read-btn" onClick={() => markRead(n.id)} className="text-muted-foreground hover:text-primary self-start">
                  <CheckCircle size={17} />
                </button>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
